import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Box, Stack } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useLocation, useSearchParams } from 'react-router-dom'
import { TicketsBulkAiReleaseModal } from '@/components/tickets/TicketsBulkAiReleaseModal'
import { TicketsBulkReplyModal } from '@/components/tickets/TicketsBulkReplyModal'
import { TicketsBulkStatusModal } from '@/components/tickets/TicketsBulkStatusModal'
import { displayNameUser, TicketsStripBlock, TicketsStripHead } from '@/components/tickets/TicketsListStrips'
import { OperatorShiftStatusBadge } from '@/components/tickets/OperatorShiftStatusBadge'
import { refreshTicketPreviewModeFromApi } from '@/lib/workspace-ticket-preview-mode'
import { TicketsListTable, type TicketsListBulkSelectConfig } from '@/components/tickets/TicketsListTable'
import { TicketsListToolbar } from '@/components/tickets/TicketsListToolbar'
import { DataTableScroll, EmptyState, PageCenterLoader, PageShell, PageToolbar, PaginationBar } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { groupTicketsForDisplay } from '@/lib/group-tickets-for-display'
import { useTicketBarStats } from '@/lib/ticket-bar-stats-context'
import { useTicketsListColumnVisibility } from '@/lib/tickets-list-columns'
import {
  useOpenTicketFromList,
  usePersistTicketListScrollPosition,
  useRestoreTicketsListScrollWhenReady,
} from '@/lib/open-ticket-navigation'
import { ticketsParamsFromSearch } from '@/lib/tickets-list-api-params'
import {
  TICKETS_PAGE_SIZE_DEFAULT,
  parseTicketsPageSize,
  type TicketsPageSizeOption,
} from '@/lib/tickets-list-page-size'
import {
  TICKETS_LIST_PAGE_QUERY,
  parseTicketsListPageFromSearch,
  stripTicketsListPageParam,
} from '@/lib/tickets-list-page-param'
import { buildTagsByProjectIdFromProjects } from '@/lib/project-tags-map'
import { TICKETS_LIST_INVALIDATE_STORAGE_KEY, TICKETS_LIST_STALE_EVENT } from '@/lib/projects-events'
import { getWebSocketUrl } from '@/lib/ws-url'
import { loadTicketsSelectionIds, saveTicketsSelectionIds } from '@/lib/tickets-selection-storage'
import {
  TICKETS_AI_DEFAULT_SORT_ORDER,
  TICKETS_DEFAULT_SORT_BY,
  TICKETS_DEFAULT_SORT_ORDER,
  cycleTicketsSortSearchParams,
  parseTicketsSortOrder,
  type TicketsSortByKey,
} from '@/lib/tickets-list-sort'
import { useWorkspaceTicketsSortDefaults } from '@/lib/use-workspace-tickets-sort-defaults'
import type {
  BlockTotals,
  ProjectRow,
  ProjectTagRow,
  ShiftStatusBadgeRow,
  TicketListItem,
  WorkspaceUserRow,
} from '@/types/api'

const MAX_TICKETS_BULK_SELECT = 50

const BULK_SELECT_LIMIT_MESSAGE = 'Тыщ тыщь: куда ты столько сразу отмечаешь, и 50 хватит тебе'

type TicketViewer = NonNullable<TicketListItem['viewers']>[number]

function normalizeTicketViewers(viewers: TicketListItem['viewers']): TicketViewer[] {
  if (!Array.isArray(viewers)) return []
  const seen = new Set<string>()
  const normalized: TicketViewer[] = []
  for (const raw of viewers) {
    if (!raw || typeof raw !== 'object') continue
    const userId = typeof raw.user_id === 'string' ? raw.user_id.trim() : ''
    const userName = typeof raw.user_name === 'string' ? raw.user_name.trim() : ''
    const key = `${userId || '?'}|${userName || '?'}`
    if (seen.has(key)) continue
    seen.add(key)
    normalized.push({
      ...(userId ? { user_id: userId } : {}),
      ...(userName ? { user_name: userName } : {}),
    })
  }
  return normalized
}

function buildViewerPresenceKey(ticketId: string, viewer: TicketViewer): string {
  const userId = typeof viewer.user_id === 'string' ? viewer.user_id.trim() : ''
  const userName = typeof viewer.user_name === 'string' ? viewer.user_name.trim() : ''
  return `${ticketId}:${userId || '?'}|${userName || '?'}`
}

export function TicketsPage() {
  const openTicketFromList = useOpenTicketFromList()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const aiRoute = location.pathname === '/ai-tickets'
  const workspaceSortDefaults = useWorkspaceTicketsSortDefaults()

  const pageFromUrl = useMemo(() => parseTicketsListPageFromSearch(location.search), [location.search])

  const effectiveSortBy = searchParams.get('sort_by') ?? workspaceSortDefaults.sortBy ?? TICKETS_DEFAULT_SORT_BY
  const effectiveSortOrder = parseTicketsSortOrder(
    searchParams.get('sort_order') ??
      (aiRoute && effectiveSortBy === 'updated_at'
        ? TICKETS_AI_DEFAULT_SORT_ORDER
        : (workspaceSortDefaults.sortOrder ?? TICKETS_DEFAULT_SORT_ORDER)),
  )

  const onTicketsSort = useCallback(
    (column: TicketsSortByKey) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          stripTicketsListPageParam(p)
          cycleTicketsSortSearchParams(p, column, {
            defaultSortBy: workspaceSortDefaults.sortBy,
            defaultSortOrderWhenMissing: aiRoute ? TICKETS_AI_DEFAULT_SORT_ORDER : workspaceSortDefaults.sortOrder,
          })
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams, aiRoute, workspaceSortDefaults.sortBy, workspaceSortDefaults.sortOrder],
  )

  /** Строка поиска из location — надёжнее, чем ссылка на URLSearchParams, для перезагрузки списка */
  const queryKey = useMemo(() => `${location.pathname}${location.search}`, [location.pathname, location.search])

  const pageSize = useMemo(() => parseTicketsPageSize(new URLSearchParams(location.search)), [location.search])

  const onTicketsPageSizeChange = useCallback(
    (size: TicketsPageSizeOption) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          stripTicketsListPageParam(p)
          if (size === TICKETS_PAGE_SIZE_DEFAULT) p.delete('page_size')
          else p.set('page_size', String(size))
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const onTicketsPageChange = useCallback(
    (nextPage: number) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (nextPage <= 1) p.delete(TICKETS_LIST_PAGE_QUERY)
          else p.set(TICKETS_LIST_PAGE_QUERY, String(nextPage))
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const [rows, setRows] = useState<TicketListItem[]>([])
  const [total, setTotal] = useState(0)
  const [totalExact, setTotalExact] = useState(true)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  /** Фоновое обновление: таблица не скрывается (без «мигания» как при полной перезагрузке). */
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [blockTotals, setBlockTotals] = useState<BlockTotals | null>(null)

  const rowsCountRef = useRef(0)

  const [usersById, setUsersById] = useState<Map<string, WorkspaceUserRow>>(() => new Map())
  const [shiftBadgeById, setShiftBadgeById] = useState<Map<string, ShiftStatusBadgeRow>>(() => new Map())
  const [shiftScheduleTz, setShiftScheduleTz] = useState<string>('Europe/Moscow')
  const [projectsById, setProjectsById] = useState<Map<string, ProjectRow>>(() => new Map())
  const [tagsByProjectId, setTagsByProjectId] = useState<Map<string, ProjectTagRow[]>>(() => new Map())

  const { visibility: columnVisibility, setColumnVisible } = useTicketsListColumnVisibility()
  const { setTicketBarStats } = useTicketBarStats()

  const [selectedIds, setSelectedIds] = useState(() => new Set(loadTicketsSelectionIds()))
  const [bulkAiReleaseOpen, setBulkAiReleaseOpen] = useState(false)
  const [bulkReplyOpen, setBulkReplyOpen] = useState(false)
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false)
  const viewerPresenceInitializedRef = useRef(false)
  const previousViewerKeysRef = useRef<Set<string>>(new Set())
  const audioContextRef = useRef<AudioContext | null>(null)

  const notifyBulkLimit = useCallback(() => {
    showNotification({ title: 'Лимит', message: BULK_SELECT_LIMIT_MESSAGE, color: 'red' })
  }, [])

  const toggleRowSelect = useCallback(
    (ticketId: string, checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (!checked) {
          next.delete(ticketId)
          saveTicketsSelectionIds(next)
          return next
        }
        if (next.size >= MAX_TICKETS_BULK_SELECT) {
          notifyBulkLimit()
          return prev
        }
        next.add(ticketId)
        saveTicketsSelectionIds(next)
        return next
      })
    },
    [notifyBulkLimit],
  )

  const toggleStripSelect = useCallback(
    (rowIds: string[], checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (!checked) {
          for (const id of rowIds) next.delete(id)
          saveTicketsSelectionIds(next)
          return next
        }
        let remaining = MAX_TICKETS_BULK_SELECT - next.size
        const wantedNew = rowIds.filter((id) => !prev.has(id))
        for (const id of rowIds) {
          if (next.has(id)) continue
          if (remaining <= 0) break
          next.add(id)
          remaining--
        }
        const addedNew = wantedNew.filter((id) => next.has(id)).length
        if (wantedNew.length > 0 && addedNew < wantedNew.length) {
          notifyBulkLimit()
        }
        saveTicketsSelectionIds(next)
        return next
      })
    },
    [notifyBulkLimit],
  )

  const clearTicketSelection = useCallback(() => {
    const empty = new Set<string>()
    setSelectedIds(empty)
    saveTicketsSelectionIds(empty)
  }, [])

  const bulkSelectBase = useMemo((): Omit<TicketsListBulkSelectConfig, 'stripKey'> | null => {
    return {
      selectedIds,
      onRowToggle: toggleRowSelect,
      onStripToggle: toggleStripSelect,
    }
  }, [selectedIds, toggleRowSelect, toggleStripSelect])

  const withStripKey = useCallback(
    (stripKey: string): TicketsListBulkSelectConfig | null => (bulkSelectBase ? { ...bulkSelectBase, stripKey } : null),
    [bulkSelectBase],
  )

  const selectedIdList = useMemo(() => Array.from(selectedIds), [selectedIds])

  const refreshTicketBarCounts = useCallback(
    async (soft?: boolean) => {
      if (!soft) setTicketBarStats(null, true)
      try {
        const r = await api.ticketCounts()
        if (aiRoute && r.ai) {
          setTicketBarStats(
            {
              total: Number(r.ai.total_tickets ?? r.ai.total ?? 0) || 0,
              requireProcessing: Number(r.ai.require_processing ?? 0) || 0,
            },
            false,
          )
        } else if (r.workspace) {
          setTicketBarStats(
            {
              total: Number(r.workspace.total_tickets ?? 0) || 0,
              requireProcessing: Number(r.workspace.require_processing ?? 0) || 0,
            },
            false,
          )
        } else {
          setTicketBarStats(null, false)
        }
      } catch {
        setTicketBarStats(null, false)
      }
    },
    [aiRoute, setTicketBarStats],
  )

  useEffect(() => {
    void refreshTicketBarCounts(false)
  }, [refreshTicketBarCounts])

  useEffect(() => {
    return () => setTicketBarStats(null, false)
  }, [setTicketBarStats])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const [ul, pl] = await Promise.all([
          api.workspaceUsers().catch(() => [] as WorkspaceUserRow[]),
          api.projects().catch(() => [] as ProjectRow[]),
        ])
        if (!alive) return
        setUsersById(new Map(ul.map((u) => [u.id, u])))
        setProjectsById(new Map(pl.map((p) => [p.id, p])))
        setTagsByProjectId(buildTagsByProjectIdFromProjects(pl))
      } catch {
        /* ignore */
      }
    })()
    return () => {
      alive = false
    }
    // Re-run after auth/user becomes available (SPA navigation without hard reload).
  }, [user?.id])

  // Актуализируем режим превью тикета (саммари / сообщения клиента) из настроек workspace.
  useEffect(() => {
    if (!user?.id) return
    void refreshTicketPreviewModeFromApi().catch(() => {
      /* режим превью не критичен — используем кэш/значение по умолчанию */
    })
  }, [user?.id])

  // Статусы смен операторов для плашек в заголовках групп (с периодическим обновлением).
  useEffect(() => {
    if (!user?.id) return
    let alive = true
    const load = async () => {
      try {
        const r = await api.scheduleShiftStatusBadges()
        if (!alive) return
        setShiftBadgeById(new Map(r.items.map((it) => [it.user_id, it])))
        if (r.schedule_timezone) setShiftScheduleTz(r.schedule_timezone)
      } catch {
        /* индикатор статуса не критичен — тихо игнорируем */
      }
    }
    void load()
    const id = window.setInterval(() => void load(), 45_000)
    return () => {
      alive = false
      window.clearInterval(id)
    }
  }, [user?.id])

  const ticketsLoadSeq = useRef(0)
  const scheduledRefreshTimerRef = useRef<number | null>(null)
  const lastListRefreshAtRef = useRef(0)
  const wsConnectedRef = useRef(false)

  const load = useCallback(
    async (p: number, opts?: { soft?: boolean }) => {
      const seq = ++ticketsLoadSeq.current
      const soft = Boolean(opts?.soft)
      if (soft) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)
      try {
        const params = new URLSearchParams(location.search)
        const base = ticketsParamsFromSearch(params, { aiRoute, sortDefaults: workspaceSortDefaults })
        const ps = parseTicketsPageSize(params)
        const metaPromise = api.tickets({
          ...base,
          page: 1,
          page_size: 1,
          include_counts: false,
          counts_only: true,
        })
        const res = await api.tickets({ ...base, page: p, page_size: ps, include_counts: false })
        if (seq !== ticketsLoadSeq.current) return
        const list = Array.isArray(res.tickets)
          ? res.tickets.map((row) => ({ ...row, viewers: normalizeTicketViewers(row.viewers) }))
          : []
        setRows(list)
        // Первичная подгрузка актуальных viewers (в API списка они могут быть пустыми).
        // WS обновляет дальше, но без batch-запроса мы не узнаем, кто уже смотрит "прямо сейчас".
        if (list.length > 0) {
          const ids = list.map((x) => x.id).filter(Boolean)
          void api
            .ticketViewersBatch(ids)
            .then((r) => {
              if (seq !== ticketsLoadSeq.current) return
              const byTicket = r?.viewers ?? {}
              setRows((prev) =>
                prev.map((row) => {
                  const v = byTicket[row.id]
                  if (!v) return row
                  return { ...row, viewers: normalizeTicketViewers(v as unknown as TicketListItem['viewers']) }
                }),
              )
            })
            .catch(() => {
              /* ignore viewers batch errors */
            })
        }
        if (res.total_exact !== false) {
          setTotal(typeof res.total === 'number' && Number.isFinite(res.total) ? res.total : 0)
          setTotalExact(true)
          setBlockTotals(res.block_totals ?? null)
        } else {
          // Быстрый ответ содержит нижнюю границу total. Показываем её сразу,
          // пока отдельный запрос counts_only получает точное значение.
          setTotal(typeof res.total === 'number' && Number.isFinite(res.total) ? res.total : 0)
          setTotalExact(false)
          setBlockTotals(null)
        }
        setPage(typeof res.page === 'number' && res.page >= 1 ? res.page : p)
        void metaPromise
          .then((meta) => {
            if (seq !== ticketsLoadSeq.current) return
            setTotal(typeof meta.total === 'number' && Number.isFinite(meta.total) ? meta.total : 0)
            setTotalExact(meta.total_exact !== false)
            setBlockTotals(meta.block_totals ?? null)
          })
          .catch(() => {
            if (seq !== ticketsLoadSeq.current) return
            if (res.total_exact === false) setTotalExact(false)
          })
      } catch (e) {
        if (seq !== ticketsLoadSeq.current) return
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить обращения')
        if (!soft) {
          setRows([])
          setTotal(0)
          setTotalExact(true)
          setBlockTotals(null)
        }
      } finally {
        if (seq === ticketsLoadSeq.current) {
          setLoading(false)
          setRefreshing(false)
        }
      }
    },
    [location.search, aiRoute, workspaceSortDefaults],
  )

  // Периодически обновляем viewers для видимых тикетов: это даёт "актуально на сейчас"
  // даже если вкладку карточки закрыли без /close (тогда исчезновение произойдёт по timeout),
  // или если WS был недоступен.
  const viewersIdsRef = useRef<string[]>([])
  useEffect(() => {
    viewersIdsRef.current = Array.isArray(rows) ? rows.map((x) => x.id).filter(Boolean) : []
  }, [rows])
  useEffect(() => {
    let stopped = false
    const tick = async () => {
      if (stopped) return
      if (document.visibilityState !== 'visible') return
      if (wsConnectedRef.current) return
      const ids = viewersIdsRef.current
      if (!ids || ids.length === 0) return
      try {
        const r = await api.ticketViewersBatch(ids)
        if (stopped) return
        const byTicket = r?.viewers ?? {}
        setRows((prev) => {
          let changed = false
          const next = prev.map((row) => {
            const v = byTicket[row.id]
            if (!v) return row
            const normalized = normalizeTicketViewers(v as unknown as TicketListItem['viewers'])
            const current = normalizeTicketViewers(row.viewers)
            const same =
              current.length === normalized.length &&
              current.every(
                (cv, i) => cv.user_id === normalized[i]?.user_id && cv.user_name === normalized[i]?.user_name,
              )
            if (same) return row
            changed = true
            return { ...row, viewers: normalized }
          })
          return changed ? next : prev
        })
      } catch {
        /* ignore */
      }
    }
    const timer = window.setInterval(() => void tick(), 20000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    void load(pageFromUrl, { soft: false })
  }, [queryKey, load])

  usePersistTicketListScrollPosition()
  useRestoreTicketsListScrollWhenReady(location.pathname, location.search, !loading && !refreshing)

  const refreshListOnly = useCallback(() => {
    const p = pageFromUrl
    const soft = p > 1 || rowsCountRef.current > 0
    lastListRefreshAtRef.current = Date.now()
    void load(p, { soft })
  }, [load, pageFromUrl])

  const scheduleRefreshListOnly = useCallback(
    (delayMs = 250, minIntervalMs = 1500) => {
      if (scheduledRefreshTimerRef.current != null) return
      const elapsed = Date.now() - lastListRefreshAtRef.current
      const actualDelay = elapsed >= minIntervalMs ? delayMs : Math.max(delayMs, minIntervalMs - elapsed)
      scheduledRefreshTimerRef.current = window.setTimeout(() => {
        scheduledRefreshTimerRef.current = null
        refreshListOnly()
      }, actualDelay)
    },
    [refreshListOnly],
  )

  const playViewersPresenceSound = useCallback(() => {
    if (typeof window === 'undefined') return
    const AudioCtx = window.AudioContext
    if (!AudioCtx) return
    try {
      const ctx = audioContextRef.current ?? new AudioCtx()
      audioContextRef.current = ctx
      if (ctx.state === 'suspended') {
        void ctx.resume().catch(() => undefined)
      }
      const now = ctx.currentTime
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(940, now)
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.055, now + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(now)
      osc.stop(now + 0.2)
    } catch {
      /* ignore autoplay / audio errors */
    }
  }, [])

  useEffect(() => {
    const wsUrl = getWebSocketUrl('/api/v1/ws/tickets')
    let ws: WebSocket | null = null
    let stopped = false
    let reconnectTimer: number | null = null
    let pingTimer: number | null = null

    type WsTicketViewersUpdate = {
      type: 'ticket_viewers_update'
      ticket_id: string
      viewers?: TicketListItem['viewers']
    }
    type WsTicketUpdated = { type: 'ticket_updated'; ticket_id: string }
    type WsNewTicket = { type: 'new_ticket' }

    const cleanupTimers = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      if (pingTimer != null) {
        window.clearInterval(pingTimer)
        pingTimer = null
      }
    }

    const connect = () => {
      if (stopped) return
      cleanupTimers()
      try {
        ws = new WebSocket(wsUrl)
      } catch {
        reconnectTimer = window.setTimeout(connect, 2000)
        return
      }

      ws.onopen = () => {
        wsConnectedRef.current = true
        // Сервер читает входящие сообщения в цикле receive_text; ping удерживает соединение.
        pingTimer = window.setInterval(() => {
          if (!ws || ws.readyState !== WebSocket.OPEN) return
          try {
            ws.send(JSON.stringify({ type: 'ping' }))
          } catch {
            /* ignore */
          }
        }, 15000)
      }

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(String(event.data || '')) as
            WsTicketViewersUpdate | WsTicketUpdated | WsNewTicket | { type?: string }
          if (payload?.type === 'ticket_viewers_update' && 'ticket_id' in payload && payload.ticket_id) {
            setRows((prev) => {
              let changed = false
              const next = prev.map((row) => {
                if (row.id !== payload.ticket_id) return row
                const normalized = normalizeTicketViewers(payload.viewers)
                const current = normalizeTicketViewers(row.viewers)
                const same =
                  current.length === normalized.length &&
                  current.every(
                    (cv, i) => cv.user_id === normalized[i]?.user_id && cv.user_name === normalized[i]?.user_name,
                  )
                if (same) return row
                changed = true
                return { ...row, viewers: normalized }
              })
              return changed ? next : prev
            })
            return
          }
          if (payload?.type === 'ticket_updated' || payload?.type === 'new_ticket') {
            scheduleRefreshListOnly(300, 2000)
          }
        } catch {
          /* ignore malformed ws payload */
        }
      }

      ws.onclose = () => {
        wsConnectedRef.current = false
        cleanupTimers()
        ws = null
        if (!stopped) reconnectTimer = window.setTimeout(connect, 1500)
      }
      ws.onerror = () => {
        wsConnectedRef.current = false
        // onclose выполнит reconnect
      }
    }

    connect()
    return () => {
      stopped = true
      wsConnectedRef.current = false
      cleanupTimers()
      if (ws) ws.close()
    }
  }, [scheduleRefreshListOnly])

  useEffect(() => {
    const onStale = () => scheduleRefreshListOnly(220, 2000)
    const onStorage = (e: StorageEvent) => {
      if (e.key === TICKETS_LIST_INVALIDATE_STORAGE_KEY && e.newValue) onStale()
    }
    window.addEventListener(TICKETS_LIST_STALE_EVENT, onStale)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(TICKETS_LIST_STALE_EVENT, onStale)
      window.removeEventListener('storage', onStorage)
    }
  }, [scheduleRefreshListOnly])

  /** Вернулся на вкладку браузера или фокус окна — актуальный список (в т.ч. решённые исчезнут при фильтре API). */
  useEffect(() => {
    const path = location.pathname
    if (path !== '/tickets' && path !== '/ai-tickets') return
    let timer: number
    const bump = () => {
      if (document.visibilityState !== 'visible') return
      window.clearTimeout(timer)
      timer = window.setTimeout(() => scheduleRefreshListOnly(250, 3000), 400)
    }
    document.addEventListener('visibilitychange', bump)
    return () => {
      document.removeEventListener('visibilitychange', bump)
      window.clearTimeout(timer)
    }
  }, [location.pathname, scheduleRefreshListOnly])

  useEffect(() => {
    return () => {
      if (scheduledRefreshTimerRef.current != null) {
        window.clearTimeout(scheduledRefreshTimerRef.current)
        scheduledRefreshTimerRef.current = null
      }
    }
  }, [])

  const safeTotal = typeof total === 'number' && Number.isFinite(total) ? total : 0
  const safeRows = Array.isArray(rows) ? rows : []
  useEffect(() => {
    const nextKeys = new Set<string>()
    for (const row of safeRows) {
      const viewers = normalizeTicketViewers(row.viewers)
      for (const viewer of viewers) {
        nextKeys.add(buildViewerPresenceKey(row.id, viewer))
      }
    }
    const prevKeys = previousViewerKeysRef.current
    if (!viewerPresenceInitializedRef.current) {
      viewerPresenceInitializedRef.current = true
      previousViewerKeysRef.current = nextKeys
      if (nextKeys.size > 0) playViewersPresenceSound()
      return
    }
    let hasNewViewer = false
    for (const key of nextKeys) {
      if (!prevKeys.has(key)) {
        hasNewViewer = true
        break
      }
    }
    previousViewerKeysRef.current = nextKeys
    if (hasNewViewer) playViewersPresenceSound()
  }, [safeRows, playViewersPresenceSound])

  useEffect(() => {
    return () => {
      const ctx = audioContextRef.current
      audioContextRef.current = null
      if (ctx) void ctx.close().catch(() => undefined)
    }
  }, [])

  useEffect(() => {
    rowsCountRef.current = safeRows.length
  }, [safeRows.length])

  const pages = Math.max(1, Math.ceil(safeTotal / pageSize))

  const grouped = useMemo(
    () =>
      groupTicketsForDisplay(safeRows, user?.id, {
        dateSortOrder: effectiveSortOrder === 'desc' ? 'desc' : 'asc',
        resortWithinGroupsByUpdated: effectiveSortBy === 'updated_at',
      }),
    [safeRows, user?.id, effectiveSortBy, effectiveSortOrder],
  )

  const meName = user ? displayNameUser(user) : 'Оператор'

  const countMe =
    blockTotals != null && typeof blockTotals.assigned_to_me === 'number' && Number.isFinite(blockTotals.assigned_to_me)
      ? blockTotals.assigned_to_me
      : grouped.assigned_to_me.length
  const countUn =
    blockTotals != null && typeof blockTotals.unassigned === 'number' && Number.isFinite(blockTotals.unassigned)
      ? blockTotals.unassigned
      : grouped.unassigned.length
  const countOthersTotal = useMemo(() => {
    if (blockTotals?.assigned_to_others && Object.keys(blockTotals.assigned_to_others).length > 0) {
      return Object.values(blockTotals.assigned_to_others).reduce((a, b) => a + (Number(b) || 0), 0)
    }
    return Object.values(grouped.assigned_to_others).reduce((s, arr) => s + arr.length, 0)
  }, [blockTotals, grouped.assigned_to_others])

  const renderGroupedTables = () => {
    if (safeRows.length === 0) return null

    const blocks: ReactNode[] = []

    if (grouped.assigned_to_me.length > 0) {
      blocks.push(
        <TicketsStripBlock
          key="me"
          title={`Тикеты ${meName}`}
          count={countMe}
          statusSlot={
            user?.id ? (
              <OperatorShiftStatusBadge badge={shiftBadgeById.get(user.id)} scheduleTimezone={shiftScheduleTz} />
            ) : undefined
          }
        >
          <TicketsListTable
            rows={grouped.assigned_to_me}
            usersById={usersById}
            projectsById={projectsById}
            tagsByProjectId={tagsByProjectId}
            sortBy={effectiveSortBy}
            sortOrder={effectiveSortOrder}
            onSort={onTicketsSort}
            columnVisibility={columnVisibility}
            bulkSelect={withStripKey('me')}
            onRowClick={openTicketFromList}
          />
        </TicketsStripBlock>,
      )
    }

    if (grouped.unassigned.length > 0) {
      blocks.push(
        <TicketsStripBlock key="un" title="Не назначенные" count={countUn}>
          <TicketsListTable
            rows={grouped.unassigned}
            usersById={usersById}
            projectsById={projectsById}
            tagsByProjectId={tagsByProjectId}
            sortBy={effectiveSortBy}
            sortOrder={effectiveSortOrder}
            onSort={onTicketsSort}
            columnVisibility={columnVisibility}
            bulkSelect={withStripKey('un')}
            onRowClick={openTicketFromList}
          />
        </TicketsStripBlock>,
      )
    }

    const otherKeys = Object.keys(grouped.assigned_to_others)
    if (otherKeys.length > 0) {
      blocks.push(
        <Box key="others" className="tickets-strip-block tickets-strip-block--multi">
          <TicketsStripHead title="Назначенные на других" count={countOthersTotal} />
          {otherKeys.map((operatorId) => {
            const opTickets = grouped.assigned_to_others[operatorId]
            if (!opTickets.length) return null
            const op = usersById.get(operatorId)
            const opTitle = op ? displayNameUser(op) : 'Неизвестный оператор'
            const rawOp = blockTotals?.assigned_to_others?.[operatorId]
            const opCount = typeof rawOp === 'number' && Number.isFinite(rawOp) ? rawOp : opTickets.length
            return (
              <Fragment key={operatorId}>
                <TicketsStripHead
                  title={opTitle}
                  count={opCount}
                  sub
                  statusSlot={
                    <OperatorShiftStatusBadge
                      badge={shiftBadgeById.get(operatorId)}
                      scheduleTimezone={shiftScheduleTz}
                    />
                  }
                />
                <DataTableScroll className="tickets-table-scroll tickets-strip-table">
                  <TicketsListTable
                    rows={opTickets}
                    usersById={usersById}
                    projectsById={projectsById}
                    tagsByProjectId={tagsByProjectId}
                    sortBy={effectiveSortBy}
                    sortOrder={effectiveSortOrder}
                    onSort={onTicketsSort}
                    columnVisibility={columnVisibility}
                    bulkSelect={withStripKey(`op-${operatorId}`)}
                    onRowClick={openTicketFromList}
                  />
                </DataTableScroll>
              </Fragment>
            )
          })}
        </Box>,
      )
    }

    return (
      <Stack gap={0} className="tickets-page-tickets-stack">
        {blocks}
      </Stack>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <TicketsListToolbar
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              disabled={loading && safeRows.length === 0}
              listRefreshing={refreshing}
              onRefresh={() => {
                void load(pageFromUrl, { soft: safeRows.length > 0 })
                void refreshTicketBarCounts(true)
              }}
              columnVisibility={columnVisibility}
              onColumnVisibleChange={setColumnVisible}
              workspaceUsers={Array.from(usersById.values())}
              projects={Array.from(projectsById.values())}
              showProjectFilter={aiRoute}
              showAiLastMessageFilters={aiRoute}
              pageSize={pageSize}
              onPageSizeChange={onTicketsPageSizeChange}
              totalCount={totalExact ? safeTotal : undefined}
              bulkActions={
                selectedIds.size > 0
                  ? {
                      count: selectedIds.size,
                      onReply: () => setBulkReplyOpen(true),
                      onStatus: () => setBulkStatusOpen(true),
                      onReleaseAi: aiRoute ? () => setBulkAiReleaseOpen(true) : undefined,
                      onClear: clearTicketSelection,
                    }
                  : null
              }
            />
          }
        />

        {error ? (
          <Alert color="red" variant="light" title="Ошибка загрузки">
            {error}
          </Alert>
        ) : null}

        {loading && safeRows.length === 0 ? (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 240,
            }}
          >
            <PageCenterLoader label="Загрузка списка" minHeight={160} />
          </Box>
        ) : !loading && safeRows.length === 0 ? (
          <EmptyState title="Нет обращений" description="По текущим фильтрам список пуст." />
        ) : (
          <Stack gap={4} w="100%">
            {pages > 1 ? (
              <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <PaginationBar
                  page={page}
                  pageCount={pages}
                  total={safeTotal}
                  disabled={loading && safeRows.length === 0}
                  onPageChange={onTicketsPageChange}
                />
              </Box>
            ) : null}
            {aiRoute ? (
              <DataTableScroll className="tickets-table-scroll">
                <TicketsListTable
                  rows={safeRows}
                  usersById={usersById}
                  projectsById={projectsById}
                  tagsByProjectId={tagsByProjectId}
                  sortBy={effectiveSortBy}
                  sortOrder={effectiveSortOrder}
                  onSort={onTicketsSort}
                  columnVisibility={columnVisibility}
                  bulkSelect={withStripKey('ai')}
                  onRowClick={openTicketFromList}
                />
              </DataTableScroll>
            ) : (
              renderGroupedTables()
            )}
            {pages > 1 ? (
              <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <PaginationBar
                  page={page}
                  pageCount={pages}
                  total={safeTotal}
                  disabled={loading && safeRows.length === 0}
                  onPageChange={onTicketsPageChange}
                />
              </Box>
            ) : null}
          </Stack>
        )}
      </Stack>

      <TicketsBulkReplyModal
        opened={bulkReplyOpen}
        onClose={() => setBulkReplyOpen(false)}
        ticketIds={selectedIdList}
        projects={Array.from(projectsById.values())}
        onApplied={() => {
          clearTicketSelection()
          void load(pageFromUrl, { soft: safeRows.length > 0 })
          void refreshTicketBarCounts(true)
        }}
      />
      {aiRoute ? (
        <TicketsBulkAiReleaseModal
          opened={bulkAiReleaseOpen}
          onClose={() => setBulkAiReleaseOpen(false)}
          ticketIds={selectedIdList}
          onApplied={() => {
            clearTicketSelection()
            void load(pageFromUrl, { soft: safeRows.length > 0 })
            void refreshTicketBarCounts(true)
          }}
        />
      ) : null}
      <TicketsBulkStatusModal
        opened={bulkStatusOpen}
        onClose={() => setBulkStatusOpen(false)}
        ticketIds={selectedIdList}
        onApplied={() => {
          clearTicketSelection()
          void load(pageFromUrl, { soft: safeRows.length > 0 })
          void refreshTicketBarCounts(true)
        }}
      />
    </PageShell>
  )
}
